/**
 * Learner HTTP client + auth API.
 * Tokens live under student_* keys (backend is /api/student) and stay
 * isolated from teacher/admin tokens.
 *
 * Temporary email/password login — will be replaced by Grinds SSO later.
 */
import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios"
import { getSubdomain } from "../utils/getSubdomain"
import { LEARNER_LOGIN_PATH } from "../auth/learner/constants"
import {
  clearLearnerSession,
  getLearnerAccessToken,
  getLearnerRefreshToken,
  getStoredStudentInfo,
  hasLearnerSession,
  saveLearnerSession,
  updateLearnerAccessToken,
  updateStoredStudentInfo,
} from "../auth/learner/session"
import type {
  LearnerApiResponse,
  LearnerLoginData,
  StudentInfo,
  StudentLoginPayload,
} from "../auth/learner/types"

type RetryConfig = InternalAxiosRequestConfig & { _retry?: boolean }

let isRefreshing = false
let refreshWaiters: Array<{
  resolve: (token: string) => void
  reject: (error: unknown) => void
}> = []

const flushRefreshWaiters = (error: unknown, token: string | null) => {
  refreshWaiters.forEach(({ resolve, reject }) => {
    if (error || !token) reject(error)
    else resolve(token)
  })
  refreshWaiters = []
}

const redirectToLearnerLogin = () => {
  clearLearnerSession()
  const { pathname, search } = window.location
  const isLogin = pathname.startsWith(LEARNER_LOGIN_PATH)
  if (isLogin) return

  const next = encodeURIComponent(`${pathname}${search}`)
  window.location.assign(`${LEARNER_LOGIN_PATH}?next=${next}`)
}

export const axiosStudentInstance = axios.create({
  baseURL: `${process.env.REACT_APP_BASE_URL}/api/student`,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  withCredentials: true,
})

axiosStudentInstance.interceptors.request.use(
  (config) => {
    const token = getLearnerAccessToken()
    if (token) {
      config.headers["token"] = token
    }
    config.headers["subdomain"] = getSubdomain()
    return config
  },
  (error) => Promise.reject(error)
)

axiosStudentInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryConfig | undefined
    if (!originalRequest || error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error)
    }

    if (originalRequest.url?.includes("/login/")) {
      return Promise.reject(error)
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        refreshWaiters.push({ resolve, reject })
      }).then((token) => {
        originalRequest.headers["token"] = token
        return axiosStudentInstance(originalRequest)
      })
    }

    originalRequest._retry = true
    isRefreshing = true

    try {
      const refreshToken = getLearnerRefreshToken()
      if (!refreshToken) throw new Error("No learner refresh token")

      const response = await axios.post<LearnerApiResponse<{ access_token: string }>>(
        `${process.env.REACT_APP_BASE_URL}/api/student/token/refresh/`,
        {},
        {
          params: { refresh_token: refreshToken },
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            subdomain: getSubdomain(),
          },
          withCredentials: true,
        }
      )

      const newToken = response.data?.data?.access_token
      if (!newToken) throw new Error("Refresh response missing access token")

      updateLearnerAccessToken(newToken)
      flushRefreshWaiters(null, newToken)
      originalRequest.headers["token"] = newToken
      return axiosStudentInstance(originalRequest)
    } catch (refreshError) {
      flushRefreshWaiters(refreshError, null)
      redirectToLearnerLogin()
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)

export type { StudentInfo, StudentLoginPayload, LearnerLoginData }
export {
  clearLearnerSession as clearStudentSession,
  getStoredStudentInfo,
  hasLearnerSession as isStudentLoggedIn,
  saveLearnerSession as saveStudentSession,
  updateStoredStudentInfo,
}

/** Temporary email/password login (pre-SSO). */
export const studentLogin = (data: StudentLoginPayload) =>
  axiosStudentInstance.post<LearnerApiResponse<LearnerLoginData>>("/login/", data)

/** Authenticated learner profile. */
export const getStudentProfile = () =>
  axiosStudentInstance.get<LearnerApiResponse<StudentInfo>>("/me/")

/** Validate session against /me/ and sync cached profile. */
export const validateLearnerSession = async (): Promise<StudentInfo> => {
  const res = await getStudentProfile()
  if (!res.data?.success || !res.data?.data) {
    throw new Error(res.data?.message || "Unable to validate learner session")
  }
  updateStoredStudentInfo(res.data.data)
  return res.data.data
}
